/**
 * @file        src/lib/hotelPricing.js
 * @description The admin auto hotel-discount + coupon arithmetic, for price
 *              panels that show a hotel total BEFORE the payment page (the Hotel
 *              Detail "Fare Breakup" popup). Copied verbatim from HotelPayment's
 *              bestHotelDiscount / computeCoupon / stacking, which mirror the
 *              server's hotelPricingService — so the discount previewed is the
 *              discount charged. Display only; the server recomputes at initiate.
 * @author      Kinetika Labs
 * @created     2026-10-05
 * @changelog
 *   - 2026-10-25 Claude — priceHotelCheckout rounds `fp` to the paise first, as the
 *                          server rounds its convBase, so a preview built on a raw FP
 *                          cannot land a paisa away from the charge.
 *   - 2026-10-05 Claude — Initial.
 */

/** 2-dp rupees, coercing null/NaN to 0 (HotelPayment's `money`). */
export const money = (v) => Math.round((Number(v) || 0) * 100) / 100;

/** Best auto hotel-discount for `base`: { amount, rule }. No airline scope. */
export function bestHotelDiscount(rules, base) {
  let best = { amount: 0, rule: null };
  for (const d of (rules || [])) {
    if (base < Number(d.minBookingAmount || 0)) continue;
    let amt = d.discountType === 'percentage' ? (base * Number(d.discountValue)) / 100 : Number(d.discountValue);
    if (d.maxDiscount != null) amt = Math.min(amt, Number(d.maxDiscount));
    amt = money(Math.max(0, amt));
    if (amt > best.amount) best = { amount: amt, rule: d };
  }
  return best;
}

/** Coupon discount for a { discountType, discountValue } row against `base`. */
export function hotelCouponDiscount(row, base) {
  if (!row) return 0;
  const amt = row.discountType === 'percentage' ? (base * Number(row.discountValue)) / 100 : Number(row.discountValue);
  return money(Math.max(0, amt));
}

/**
 * Fee + auto discount + coupon on a room's marked-up price `fp`, as HotelPayment
 * charges them: both discounts are taken on `fp` and STACK, trimmed (coupon
 * first, then auto) only so the total can never go negative; the convenience
 * fee is added on top.
 */
export function priceHotelCheckout({ fp: rawFp, convenienceFee = 0, rules = [], coupon = null }) {
  const fp = money(rawFp);
  const best = bestHotelDiscount(rules, fp);
  let discount = best.amount;
  let couponDiscount = hotelCouponDiscount(coupon, fp);
  if (money(discount + couponDiscount) > fp) {
    couponDiscount = Math.min(couponDiscount, fp);
    discount = Math.max(0, money(fp - couponDiscount));
  }
  const totalDiscount = money(couponDiscount + discount);
  return {
    discount,
    discountRule: best.rule,
    couponDiscount,
    totalDiscount,
    total: Math.max(1, money(fp + (Number(convenienceFee) || 0) - totalDiscount)),
  };
}
